"use client";

import { createContext, use } from "react";
import { ISbStoryData } from "@storyblok/react/rsc";
import { ISbStory } from "storyblok-js-client";
import type { PhoneCountryCode, ShareLinks } from "@/generated/sb";
import type { GlobalTranslationItem } from "@/generated/sb";
import { BreadcrumbItem } from "@/lib/storyblok/api";

export interface GlobalDataContextValues {
  version: "draft" | "published";
  domain: string;
  allResolvedLinks: ISbStory["data"]["links"];
  globalComponentsStories: ISbStoryData[];
  globalPhoneCountryCodes: PhoneCountryCode[];
  globalTranslations: GlobalTranslationItem[];
  globalShareLinks: ShareLinks | null;
  notificationBanner: ISbStoryData | null;
  breadcrumbs: BreadcrumbItem[];
  currentSlug?: string;
}

export interface GlobalDataContextProviderProps
  extends GlobalDataContextValues {
  children: React.ReactNode;
}

export const GlobalDataContext = createContext<GlobalDataContextValues>({
  version: "published",
  domain: "",
  allResolvedLinks: [],
  globalTranslations: [],
  globalComponentsStories: [],
  globalPhoneCountryCodes: [],
  globalShareLinks: null,
  notificationBanner: null,
  breadcrumbs: [],
  currentSlug: undefined,
});

export function GlobalDataContextProvider({
  children,
  version,
  domain,
  allResolvedLinks,
  globalComponentsStories,
  globalPhoneCountryCodes,
  globalTranslations,
  globalShareLinks,
  notificationBanner,
  breadcrumbs,
  currentSlug,
}: GlobalDataContextProviderProps) {
  return (
    <GlobalDataContext
      value={{
        version,
        domain,
        allResolvedLinks,
        globalComponentsStories,
        globalTranslations,
        globalPhoneCountryCodes,
        globalShareLinks,
        notificationBanner,
        breadcrumbs,
        currentSlug,
      }}
    >
      {children}
    </GlobalDataContext>
  );
}

export const useGlobalDataContext = () => {
  return use(GlobalDataContext);
};
